// src/features/system/iam-admin/components/MembershipRow.tsx
// ────────────────────────────────────────────────────────────────────────────
// Single membership row with inline status/default toggles + retire action.
// Pure renderer — receives callbacks from parent hook.
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { CARD, TEXT, BADGE, BTN, statusBadge } from '@/shared/dsp/cardTokens';
import type { Membership, MembershipStatus } from '../api/iamAdminApi';

export interface MembershipRowProps {
  m:             Membership;
  isMutating?:   boolean;
  onChangeStatus?:    (membershipRef: string, status: MembershipStatus) => void;
  onToggleDefault?:   (membershipRef: string, next: boolean) => void;
  onRetire?:          (membershipRef: string) => void;
}

const STATUS_OPTIONS: MembershipStatus[] = ['active', 'inactive', 'locked'];

export default function MembershipRow({
  m, isMutating, onChangeStatus, onToggleDefault, onRetire,
}: MembershipRowProps) {
  return (
    <div
      className={`${CARD.section} ${CARD.rowHover}`}
      data-testid={`membership-row-${m.idMembership}`}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Left: identity */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={TEXT.primaryBold}>
              {m.tenantName ?? `tenant#${m.tenantId}`}
            </span>
            <span className={`${BADGE.base} ${BADGE.neutral}`}>#{m.tenantId}</span>
            {m.isDefaultBusiness && (
              <span className={`${BADGE.base} ${BADGE.violet}`} title="default business">
                default
              </span>
            )}
            {m.isRetired && (
              <span className={`${BADGE.base} ${BADGE.neutral}`}>retired</span>
            )}
            <span className={statusBadge(m.status)}>{m.status}</span>
          </div>
          <div className={`${TEXT.secondary} mt-1`}>
            roles: <span className={TEXT.mono}>{m.roleSummary}</span>
          </div>
          <div className={`${TEXT.dim} mt-0.5`}>
            membership #{m.idMembership}
            {m.validFrom ? ` · from ${m.validFrom}` : ''}
            {m.validTo  ? ` · to ${m.validTo}`     : ''}
          </div>
        </div>

        {/* Right: controls */}
        {!m.isRetired && (
          <div className="flex flex-col items-end gap-1 shrink-0">
            <label className="flex items-center gap-1 text-[10px] text-neutral-500">
              status
              <select
                value={m.status}
                disabled={isMutating}
                onChange={e => onChangeStatus?.(m.membershipRef, e.target.value as MembershipStatus)}
                className="pac-glass-input rounded px-1 py-0.5 text-[11px] text-neutral-200"
                aria-label={`status for membership ${m.idMembership}`}
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-1 text-[10px] text-neutral-500">
              <input
                type="checkbox"
                checked={m.isDefaultBusiness}
                disabled={isMutating}
                onChange={e => onToggleDefault?.(m.membershipRef, e.target.checked)}
                aria-label={`set membership ${m.idMembership} as default business`}
              />
              default business
            </label>
            <button
              type="button"
              onClick={() => onRetire?.(m.membershipRef)}
              disabled={isMutating}
              className={BTN.danger}
              aria-label={`retire membership ${m.idMembership}`}
            >
              Retire
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
